
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/table-pagination';
import { StoredBacktestSimulation } from '@/services/ProgressiveBacktestService';
import { GrowthChart } from './GrowthChart';
import { ActualGrowthChart } from './ActualGrowthChart';
import { Download, FileText } from 'lucide-react';

interface SimulationDetailDialogProps {
  simulation: StoredBacktestSimulation | null;
  open: boolean;
  onClose: () => void;
}

export const SimulationDetailDialog: React.FC<SimulationDetailDialogProps> = ({
  simulation,
  open,
  onClose
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [tpRatio, setTpRatio] = useState(2);
  const [slRatio, setSlRatio] = useState(1);

  // Pagination for positions
  const paginatedPositions = useMemo(() => {
    if (!simulation) return [];
    const positionsData = simulation.positions_data;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return positionsData.slice(startIndex, endIndex);
  }, [simulation, currentPage, pageSize]);

  if (!simulation) return null;

  const positionsData = simulation.positions_data;

  // Calculate additional stats
  const totalPositions = positionsData.length;
  const skippedPositions = positionsData.filter(p => p.prediction === 'SKIP').length;
  const tradedPositions = totalPositions - skippedPositions;
  const correctPredictions = positionsData.filter(p => p.isCorrect === true).length;
  const incorrectPredictions = positionsData.filter(p => p.isCorrect === false).length;
  const accuracy = tradedPositions > 0 ? (correctPredictions / tradedPositions) * 100 : 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Export to CSV function
  const exportToCSV = () => {
    const headers = [
      'Position',
      'Sequence',
      'Length',
      'Prediction',
      'Actual',
      'Result',
      'Win Prob',
      'Loss Prob',
      'Confidence',
      'Patterns',
      'Reason'
    ];

    const csvContent = [
      headers.join(','),
      ...positionsData.map(position => [
        position.position,
        `"${position.sequenceUsed || ''}"`,
        position.sequenceLength || '',
        position.prediction,
        position.actualResult === 1 ? 'WIN' : 'LOSS',
        position.isCorrect ? 'Correct' : 'Incorrect',
        position.winProbability || '',
        position.lossProbability || '',
        position.confidenceLevel || '',
        position.matchingPatterns || '',
        `"${position.reason || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `simulation_${simulation.id}_results.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Simulation Details - {simulation.id}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Configuration Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Analysis Mode</p>
                  <Badge variant="outline">{simulation.analysis_mode.toUpperCase()}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Start Position</p>
                  <p className="text-sm">{simulation.start_position}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Max Sequence Length</p>
                  <p className="text-sm">{simulation.max_sequence_length}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Win Threshold</p>
                  <p className="text-sm">{simulation.win_threshold}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Summary Statistics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Summary Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Positions</p>
                  <p className="text-xl font-bold">{totalPositions}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Traded Positions</p>
                  <p className="text-xl font-bold text-blue-600">{tradedPositions}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Skipped Positions</p>
                  <p className="text-xl font-bold text-gray-600">{skippedPositions}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Prediction Accuracy</p>
                  <p className="text-xl font-bold text-green-600">{accuracy.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TP/SL Ratio Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Growth Chart Settings</CardTitle>
              <CardDescription>Configure TP/SL ratios for realistic growth calculations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Take Profit</label>
                  <input 
                    type="number" 
                    min="0.1" 
                    max="10" 
                    step="0.1" 
                    value={tpRatio}
                    className="w-full mt-1 px-3 py-1 border rounded text-sm"
                    onChange={(e) => setTpRatio(parseFloat(e.target.value) || 2)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Stop Loss</label>
                  <input 
                    type="number" 
                    min="0.1" 
                    max="10" 
                    step="0.1" 
                    value={slRatio}
                    className="w-full mt-1 px-3 py-1 border rounded text-sm"
                    onChange={(e) => setSlRatio(parseFloat(e.target.value) || 1)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prediction Growth Chart */}
          <GrowthChart positionsData={positionsData} tpRatio={tpRatio} slRatio={slRatio} />

          {/* Actual Market Growth Chart */}
          <ActualGrowthChart positionsData={positionsData} tpRatio={tpRatio} slRatio={slRatio} />

          {/* Position Results Table */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base">Position Results</CardTitle>
                  <CardDescription>
                    Detailed analysis results for each position with pattern data
                  </CardDescription>
                </div>
                <Button onClick={exportToCSV} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[8%]">Pos</TableHead>
                      <TableHead className="w-[15%]">Sequence</TableHead>
                      <TableHead className="w-[6%]">Len</TableHead>
                      <TableHead className="w-[10%]">Pred</TableHead>
                      <TableHead className="w-[8%]">Actual</TableHead>
                      <TableHead className="w-[8%]">Result</TableHead>
                      <TableHead className="w-[8%]">Win%</TableHead>
                      <TableHead className="w-[8%]">Loss%</TableHead>
                      <TableHead className="w-[10%]">Conf</TableHead>
                      <TableHead className="w-[8%]">Pat</TableHead>
                      <TableHead className="w-[11%]">Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedPositions.map((position, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium text-xs">#{position.position}</TableCell>
                        <TableCell className="p-2">
                          <div className="truncate font-mono text-xs bg-muted px-1 py-0.5 rounded max-w-full" title={position.sequenceUsed || 'N/A'}>
                            {position.sequenceUsed || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{position.sequenceLength || 'N/A'}</TableCell>
                        <TableCell className="p-2">
                          <Badge variant={
                            position.prediction === 'WIN' ? 'default' :
                            position.prediction === 'LOSS' ? 'destructive' : 'secondary'
                          } className="text-xs px-1 py-0.5">
                            {position.prediction}
                          </Badge>
                        </TableCell>
                        <TableCell className="p-2">
                          <Badge variant={position.actualResult === 1 ? 'default' : 'destructive'} className="text-xs px-1 py-0.5">
                            {position.actualResult === 1 ? 'WIN' : 'LOSS'}
                          </Badge>
                        </TableCell>
                        <TableCell className="p-2">
                          {position.prediction !== 'SKIP' ? (
                            <Badge variant={position.isCorrect ? 'default' : 'destructive'} className="text-xs px-1 py-0.5">
                              {position.isCorrect ? '✓' : '✗'}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {position.winProbability ? `${position.winProbability.toFixed(1)}%` : 'N/A'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {position.lossProbability ? `${position.lossProbability.toFixed(1)}%` : 'N/A'}
                        </TableCell>
                        <TableCell className="p-2">
                          <Badge variant="outline" className="text-xs px-1 py-0.5">
                            {position.confidenceLevel || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-medium">
                          {position.matchingPatterns || 0}
                        </TableCell>
                        <TableCell className="p-2">
                          <div className="truncate text-xs text-muted-foreground max-w-full" title={position.reason || 'No reason provided'}>
                            {position.reason || 'No reason provided'}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <TablePagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalPositions}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[25, 50, 100]}
                className="mt-4"
              />
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
